import React from 'react';
import _ from 'lodash';

import SpotWorkersContext from './SpotWorkersContext';

import LocalStorageService from '../utilities/LocalStorageService';
import SessionStorageService from '../utilities/SessionStorageService';

import {
  WORKER_IMAGE_CCUS_SYNCED_STATUS,
  SORT_TYPE,
  SPOT_WORKER_STATUS,
  SPOT_WORKER_TYPE,
  WORKER_IMAGE_REGISTER_STATUS,
} from '../../utilities/constants';
import FieldContractorUserService from '../FieldContractorUsers';
import { isNullOrEmpty } from '../../utilities/stringUtil';
import SpotWorkersApi from './SpotWorkersApi';
import SpotWorkersService from './SpotWorkersService';
import WorkerSettingsApi from '../workerSettings/WorkerSettingsApi';
import FieldSettingsApi from '../fieldSettings/FieldSettingsApi';
import FieldWorkerImageStatusesApi from '../fieldWorkerImageStatuses/FieldWorkerImageStatusesApi';
import { extractErrorMessage } from '../../utilities/commonUtil';

const DIALOG_STATUS = {
  disabled: 0,
  registerResidentWorkersEnabled: 1,
  registrationCompleteEnabled: 2,
  updateStatusDialogEnabled: 3,
  integrationStartedDialogEnabled: 4,
  integrationSucceedDialogEnabled: 5,
  deleteSpotWorkersDialogEnabled: 7,
};

const DEFAULT_SEARCH_PARAM = {
  // 検索フォーム用プロパティ
  total: 0, // 検索結果件数
  offset: 0, // 検索時の初期件数
  limit: null,
  spotWorkerName: '', // 氏名（かな検索可）
  companyName: '', // 会社名
  workerImageRegisterStatusList: _.values(WORKER_IMAGE_REGISTER_STATUS), // 顔写真 (0: 無、1: 有)
  workerImageCcusSyncedStatusList: _.filter(
    WORKER_IMAGE_CCUS_SYNCED_STATUS,
    'canSearch',
  ), // 顔認証機器連携 (0: 未登録、1: 登録済、-1: 送信対象外)
  hasErrorList: [], // エラー状況 (0: 無、1: 有、2: すべて)
  statusList: _.values(SPOT_WORKER_STATUS), // ステータス（0: 稼働中、1: 停止中）
  workerType: [],
  order: '', // ソート項目
  sortType: '',
  isOpened: false, // 検索フォームトグルの開閉フラグ
};

/**
 * 現場常駐社員・非常駐入退場者一覧のstate管理用コンポーネント
 * */
export default class SpotWorkersStore extends React.Component {
  state = {
    isLoading: false, // API等でローディング中フラグ
    isShowNotExistFieldIdDialog: false, // 現場IDの存在チェックフラグ
    roleId: LocalStorageService.getRoleId(), // ログイン中ユーザーの権限
    canUseFaceRegisterButton: false, // 顔画像登録ボタン活性フラグ
    isVisibleThumbnails: false,
    dialogStatus: DIALOG_STATUS.disabled,
    registerResidentWorkersDialog: {
      users: [],
      selectedUserIds: [],
      offset: 0,
      total: 0,
    },
    errorDialog: {
      enabled: false,
      message: '',
      title: '',
    },
    updateStatusDialog: {
      targetWorker: null,
      selectedValue: SPOT_WORKER_STATUS.IN_OPERATION,
    },
    searchForm: _.cloneDeep(DEFAULT_SEARCH_PARAM),
    currentSearchParams: _.cloneDeep(DEFAULT_SEARCH_PARAM),
    spotWorkers: [],
    selectedSpotWorkers: [],
    primaryCompanyName: '',
    canLoadMore: true,
  };

  /**
   * 検索フォームの開閉トグル
   * */
  searchFormHandle = () => {
    this.setState(state => {
      let searchForm = { ...state.searchForm };
      searchForm.isOpened = !searchForm.isOpened;
      return { searchForm };
    });
  };

  /**
   * 検索条件をクリアする
   * @param {boolean} clearSort ソートをクリアするか？
   * */
  clearSearchProperty = (clearSort = true) => {
    if (this.state.isLoading === false) {
      let searchForm = { ...this.state.searchForm };
      searchForm.spotWorkerName = '';
      searchForm.companyName = '';
      searchForm.workerImageRegisterStatusList = _.values(
        WORKER_IMAGE_REGISTER_STATUS
      );
      searchForm.workerImageCcusSyncedStatusList = _.filter(
        WORKER_IMAGE_CCUS_SYNCED_STATUS,
        'canSearch'
      );
      searchForm.hasErrorList = [];
      searchForm.statusList = _.values(SPOT_WORKER_STATUS);
      searchForm.workerType = _.filter(SPOT_WORKER_TYPE, {
        residenceTypeId: this.props.spotWorkerType.id,
      });

      if (clearSort) {
        searchForm.order = '';
        searchForm.sortType = '';
      }

      this.setState({ searchForm, currentSearchParams: _.cloneDeep(searchForm) }, async () => {
        await this.clearSpotWorkers();
        await this.loadSpotWorkers();
      });
    }
  };

  /**
   * ソート
   * @param {string} sortName ソートしたい名前
   * */
  changeSort = sortName => {
    if (this.state.isLoading === false) {
      this.setState(
        state => {
          let searchForm = { ...state.searchForm };
          if (searchForm.order === sortName) {
            if (searchForm.sortType === SORT_TYPE.asc.id) {
              searchForm.sortType = SORT_TYPE.desc.id;
            } else {
              searchForm.sortType = SORT_TYPE.asc.id;
            }
          } else {
            searchForm.order = sortName;
            searchForm.sortType = SORT_TYPE.asc.id;
          }
          return {
            searchForm,
            currentSearchParams: {
              ...state.currentSearchParams,
              order: searchForm.order,
              sortType: searchForm.sortType,
            },
          };
        },
        async () => {
          await this.clearSpotWorkers();
          await this.loadSpotWorkers();
        }
      );
    }
  };

  /**
   * 検索フォームの氏名を入力
   * @param {object} value 入力された氏名
   * */
  inputSpotWorkerName = value => {
    let searchForm = { ...this.state.searchForm };
    searchForm.spotWorkerName = value;
    this.setState({ searchForm });
  };

  /**
   * 検索フォームの会社名を入力
   * @param {object} value 入力された会社名
   * */
  inputCompanyName = value => {
    let searchForm = { ...this.state.searchForm };
    searchForm.companyName = value;
    this.setState({ searchForm });
  };

  /**
   * チェックボックス1件更新
   * @param {string} propertyName ステート内のプロパティ名
   * @param {object} item 1件のオブジェクト(選択した項目)
   * */
  checkSingleData = (propertyName, item) => {
    if (!item.hasOwnProperty('id') && !item.hasOwnProperty('ID')) {
      return;
    }

    this.setState(state => {
      let searchForm = _.cloneDeep(state.searchForm);
      if (_.some(searchForm[propertyName], item)) {
        searchForm[propertyName] = searchForm[propertyName].filter(
          old =>
            (old.hasOwnProperty('id') && old.id !== item.id) ||
            (old.hasOwnProperty('ID') && old.ID !== item.ID)
        );
      } else {
        searchForm[propertyName].push(item);
      }
      return { searchForm };
    });
  };

  doSearch = async () => {
    if (this.state.isLoading === false) {
      await this.clearSpotWorkers();
      this.setState(
        prevState => ({
          currentSearchParams: _.cloneDeep(prevState.searchForm),
        }),
        this.loadSpotWorkers,
      );
    }
  };

  toggleThumbnails = () => {
    this.setState(state => {
      return { isVisibleThumbnails: !state.isVisibleThumbnails };
    });
  };

  canExecuteIntegration = () => {
    return this.state.canUseFaceRegisterButton;
  };

  executeIntegration = async () => {
    this.setState({ isLoading: true });

    try {
      const response = await WorkerSettingsApi.registerFaceImages(
        this.state.fieldId
      );
      if (response.result) {
        this.setState({
          dialogStatus: DIALOG_STATUS.integrationStartedDialogEnabled,
        });
      } else {
        this.showErrorDialog(response.message);
      }
    } catch (error) {
      alert(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  /**
   * 現場情報が存在しないエラーダイアログを閉じる
   * */
  closeNotExistFieldIdDialog = () => {
    this.setState({ isShowNotExistFieldIdDialog: false });
  };

  resetRegisterResidentWorkersDialog = (callback = null) => {
    this.setState(
      {
        registerResidentWorkersDialog: {
          users: [],
          selectedUserIds: [],
          offset: 0,
          total: 0,
        },
      },
      callback
    );
  };

  handleSpotWorkerSelected = worker => {
    this.setState(state => {
      let selectedSpotWorkers = _.clone(state.selectedSpotWorkers);
      if (_.some(selectedSpotWorkers, worker)) {
        _.pull(selectedSpotWorkers, worker);
      } else {
        selectedSpotWorkers.push(worker);
      }
      return { selectedSpotWorkers };
    });
  };

  handleRegisterResidentWorkersMenuButtonClicked = async () => {
    this.resetRegisterResidentWorkersDialog(async () => {
      this.setState({
        dialogStatus: DIALOG_STATUS.registerResidentWorkersEnabled,
      });
      await this.loadUsers();
    });
  };

  closeDialog = () => {
    this.setState({ dialogStatus: DIALOG_STATUS.disabled });
  };

  closeErrorDialog = () => {
    this.setState({
      errorDialog: {
        enabled: false,
        message: '',
        title: '',
      },
    });
  };

  handleUpdateStatusConfirmButtonClicked = async () => {
    await this.updateStatus();
    this.closeDialog();
  };

  handleRegisterResidentWorkersConfirmButtonClicked = async () => {
    if (this.canConfirmRegisterResidentWorkers() === false) {
      this.showErrorDialog('登録対象の常駐社員を選択してください');
      return;
    }

    const succeed = await this.registerUsers();
    if (succeed) {
      this.setState(
        {
          dialogStatus: DIALOG_STATUS.registrationCompleteEnabled,
        },
        async () => {
          await this.clearSpotWorkers();
          await this.loadSpotWorkers();
        }
      );
    }
  };

  handleRegistrationCompleteCloseButtonClicked = () => {
    this.setState({ dialogStatus: DIALOG_STATUS.disabled });
  };

  handleDeleteSpotWorkersConfirmButtonClicked = async () => {
    await this.deleteSpotWorkers();
    this.closeDialog();
    await this.clearSpotWorkers();
    await this.loadSpotWorkers();
  };

  showDeleteSpotWorkersDialog = () => {
    if (this.state.selectedSpotWorkers.length > 0) {
      this.setState({
        dialogStatus: DIALOG_STATUS.deleteSpotWorkersDialogEnabled,
      });
    } else {
      const name = this.props.spotWorkerType.name;
      this.showErrorDialog(
        `削除対象の${name}を選択してください`,
        '変更可能な項目が選択されていません'
      );
    }
  };

  showStatusDialog = worker => {
    this.setState({
      dialogStatus: DIALOG_STATUS.updateStatusDialogEnabled,
      updateStatusDialog: {
        targetWorker: worker,
        selectedValue:
          worker.blocked_on !== null
            ? SPOT_WORKER_STATUS.SUSPENSION
            : SPOT_WORKER_STATUS.IN_OPERATION,
      },
    });
  };

  showErrorDialog = (message = null, title = '') => {
    this.setState(state => {
      const newState = { ...state.errorDialog };
      newState.enabled = true;
      if (message !== null) {
        newState.message = message;
      }
      newState.title = title;
      return { errorDialog: newState };
    });
  };

  changeStatus = status => {
    this.setState(state => {
      const newState = { ...state.updateStatusDialog };
      newState.selectedValue = status;
      return { updateStatusDialog: newState };
    });
  };

  clearSpotWorkers = () => {
    return new Promise(resolve => {
      this.setState(
        state => {
          const newState = _.cloneDeep(state);
          newState.spotWorkers = [];
          newState.selectedSpotWorkers = [];
          newState.searchForm.offset = 0;
          newState.searchForm.total = 0;
          newState.currentSearchParams.offset = 0;
          newState.currentSearchParams.total = 0;
          newState.canLoadMore = true;
          return newState;
        },
        () => resolve()
      );
    });
  };

  clearUsers = () => {
    return new Promise(resolve => {
      this.setState(
        {
          registerResidentWorkersDialog: {
            users: [],
            selectedUserIds: [],
            offset: 0,
            total: 0,
          },
        },
        () => resolve()
      );
    });
  };

  loadSpotWorkers = async () => {
    this.setState({ isLoading: true });
    try {
      const param = SpotWorkersService.createSearchSpotWorkerList(
        this.state.currentSearchParams,
        this.props.spotWorkerType
      );
      const response = await SpotWorkersApi.fetchSpotWorkerList(
        this.state.fieldId,
        param
      );
      if (!response.result) {
        this.showErrorDialog(response.data.message);
        return;
      }
      const spotWorkers = response.data.entities.field_spot_worker_settings;
      this.setState(state => {
        const newState = _.cloneDeep(state);
        spotWorkers.forEach(worker => {
          SpotWorkersService.processImages(worker);
          if (worker.worker_type == SPOT_WORKER_TYPE.PERMANENT.id) {
            worker.name_sei_kana = worker.name_sei_hiragana;
            worker.name_mei_kana = worker.name_mei_hiragana;
          }
          newState.spotWorkers.push(worker);
        });
        newState.searchForm.offset = newState.spotWorkers.length;
        newState.searchForm.total = response.data.total;
        newState.currentSearchParams.offset = newState.spotWorkers.length;
        newState.currentSearchParams.total = response.data.total;
        newState.primeCompanyName = response.data.entities.prime_company_name;
        newState.canLoadMore =
          _.size(newState.spotWorkers) < response.data.total;
        return newState;
      });
    } catch (error) {
      alert(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  loadUsers = async () => {
    this.setState({ isLoading: true });
    try {
      const response = await FieldContractorUserService.UnregisteredList(
        this.state.fieldId,
        { offset: this.state.registerResidentWorkersDialog.offset }
      );
      if (!response.result) {
        this.showErrorDialog(response.data.message);
        return;
      }
      const unregisteredUsers = response.data.entities.field_contractor_users;
      this.setState(state => {
        const dialogState = { ...state.registerResidentWorkersDialog };
        dialogState.users.push(...unregisteredUsers);
        dialogState.offset = dialogState.users.length;
        dialogState.total = response.data.total;
        return { registerResidentWorkersDialog: dialogState };
      });
    } catch (error) {
      alert(error.data.message);
      this.resetRegisterResidentWorkersDialog();
    } finally {
      this.setState({ isLoading: false });
    }
  };

  registerUsers = async () => {
    this.setState({ isLoading: true });
    try {
      const response = await SpotWorkersApi.registerResidents(
        this.state.fieldId,
        this.state.registerResidentWorkersDialog.selectedUserIds
      );
      if (!response.result) {
        await this.clearUsers();
        await this.loadUsers();
        this.showErrorDialog(response.data.message);
        return false;
      }
      return true;
    } catch (error) {
      alert(error.data.message);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  reloadSingle = async fieldSpotWorkerId => {
    this.setState({ isLoading: true });
    try {
      const response = await SpotWorkersApi.fetchSingle(
        this.state.fieldId,
        fieldSpotWorkerId
      );
      if (!response.result) {
        this.showErrorDialog(response.data.message);
      }

      const index = _.findIndex(this.state.spotWorkers, {
        field_spot_worker_id: fieldSpotWorkerId,
      });
      const spotWorker = response.data.entities.field_spot_worker_setting;
      SpotWorkersService.processImages(spotWorker);
      if (spotWorker.worker_type == SPOT_WORKER_TYPE.PERMANENT.id) {
        spotWorker.name_sei_kana = spotWorker.name_sei_hiragana;
        spotWorker.name_mei_kana = spotWorker.name_mei_hiragana;
      }

      this.setState(state => {
        const spotWorkers = _.cloneDeep(state.spotWorkers);
        spotWorkers.splice(index, 1);
        return { spotWorkers };
      });
      this.setState(state => {
        const spotWorkers = _.cloneDeep(state.spotWorkers);
        spotWorkers.splice(index, 0, spotWorker);
        return { spotWorkers };
      });
    } catch (error) {
      alert(error.data.message);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  updateStatus = async () => {
    this.setState({ isLoading: true });
    try {
      const response = await SpotWorkersApi.changeStatus(
        this.state.fieldId,
        this.state.updateStatusDialog.targetWorker.field_spot_worker_id,
        this.state.updateStatusDialog.selectedValue
      );
      if (!response.result) {
        this.showErrorDialog(response.data.message);
        return false;
      }

      await this.reloadSingle(
        this.state.updateStatusDialog.targetWorker.field_spot_worker_id
      );
      return true;
    } catch (error) {
      alert(error.data.message);
      return false;
    } finally {
      this.setState({ isLoading: false });
    }
  };

  deleteSpotWorkers = async () => {
    this.setState({ isLoading: true });
    try {
      const response = await SpotWorkersApi.deleteSpotWorkers(
        this.state.fieldId,
        this.state.selectedSpotWorkers.map(w => w.field_spot_worker_id)
      );
      if (!response.result) {
        this.showErrorDialog(response.data.message);
        return false;
      }
      return true;
    } catch (error) {
      alert(error.data.message);
      return false;
    } finally {
      this.setState({ isLoading: false });
    }
  };

  userSelectedHandler = user => {
    const id = user.user_id;
    this.setState(state => {
      let dialogState = { ...state.registerResidentWorkersDialog };
      if (dialogState.selectedUserIds.includes(id)) {
        _.pull(dialogState.selectedUserIds, id);
      } else {
        dialogState.selectedUserIds.push(id);
      }
      return { registerResidentWorkersDialog: dialogState };
    });
  };

  /**
   * 無限スクロールから呼び出される
   * @param {function} notify
   * */
  infiniteScrollLoad = async notify => {
    let state = { ...this.state };
    if (state.isLoading && notify) {
      notify();
      return;
    }

    await this.loadSpotWorkers();
    if (notify) {
      if (this.state.searchForm.offset == this.state.searchForm.total) {
        notify('end');
      } else {
        notify();
      }
    }
  };

  /**
   * 無限スクロールから呼び出される
   * @param {function} notify
   * */
  unregisteredUsersInfiniteScrollLoad = async notify => {
    let state = { ...this.state };
    if (state.isLoading && notify) {
      notify();
      return;
    }

    await this.loadUsers();
    if (notify) {
      if (
        this.state.registerResidentWorkersDialog.offset ==
        this.state.registerResidentWorkersDialog.total
      ) {
        notify('end');
      } else {
        notify();
      }
    }
  };

  isRegisterResidentWorkersDialogEnabled = () => {
    return (
      this.state.dialogStatus === DIALOG_STATUS.registerResidentWorkersEnabled
    );
  };

  isRegistrationCompleteDialogEnabled = () => {
    return (
      this.state.dialogStatus === DIALOG_STATUS.registrationCompleteEnabled
    );
  };

  isIntegrationStartedDialogEnabled = () => {
    return (
      this.state.dialogStatus === DIALOG_STATUS.integrationStartedDialogEnabled
    );
  };

  isUpdateStatusDialogEnabled = () => {
    return this.state.dialogStatus === DIALOG_STATUS.updateStatusDialogEnabled;
  };

  isDeleteSpotWorkersDialogEnabled = () => {
    return (
      this.state.dialogStatus === DIALOG_STATUS.deleteSpotWorkersDialogEnabled
    );
  };

  canConfirmRegisterResidentWorkers = () => {
    return this.state.registerResidentWorkersDialog.selectedUserIds.length > 0;
  };

  /**
   * React Component LifeCycle
   * マウントが行われた直後
   * */
  async componentDidMount() {
    const fieldId = SessionStorageService.getFieldId();
    if (isNullOrEmpty(fieldId)) {
      this.setState({ isShowNotExistFieldIdDialog: true });
      return;
    }

    Promise.all([
      FieldSettingsApi.fetchCheckInOutSettings(fieldId),
      FieldWorkerImageStatusesApi.registeredCount(fieldId),
    ])
      .then(responses => {
        if (!responses[0].result) {
          alert(extractErrorMessage(responses[0]));
          return;
        }
        if (!responses[1].result) {
          alert(extractErrorMessage(responses[1]));
          return;
        }
        let data_0 = responses[0].data;
        let data_1 = responses[1].data;
        let integration_service_use_flg =
          isNullOrEmpty(data_0.entities.check_in_out_setting) == false &&
          Array.isArray(
            data_0.entities.check_in_out_setting.integration_service_ids
          ) &&
          data_0.entities.check_in_out_setting.integration_service_ids.length >
            0;
        let registered_flg =
          isNullOrEmpty(data_1.entities.field_worker_image_statuses) == false &&
          isNullOrEmpty(
            data_1.entities.field_worker_image_statuses.registered_count
          ) == false &&
          data_1.entities.field_worker_image_statuses.registered_count > 0;
        if (
          SessionStorageService.getSpotFlg() === 1 &&
          (integration_service_use_flg || registered_flg)
        ) {
          this.setState({ canUseFaceRegisterButton: true });
        }
      })
      .catch(error => {
        alert(extractErrorMessage(error));
      });

    this.setState(
      state => {
        const newState = _.cloneDeep(state);
        newState.fieldId = fieldId;
        newState.searchForm.workerType = _.filter(SPOT_WORKER_TYPE, {
          residenceTypeId: this.props.spotWorkerType.id,
        });
        newState.currentSearchParams.workerType = _.cloneDeep(newState.searchForm.workerType);
        return newState;
      },
      async () => {
        await this.loadSpotWorkers();
      }
    );
  }

  render() {
    return (
      <SpotWorkersContext.Provider
        value={{
          state: this.state,
          searchForm: this.state.searchForm,
          closeNotExistFieldIdDialog: this.closeNotExistFieldIdDialog,
          toggleThumbnails: this.toggleThumbnails,
          infiniteScrollLoad: this.infiniteScrollLoad,
          isRegisterResidentWorkersDialogEnabled: this
            .isRegisterResidentWorkersDialogEnabled,
          isRegistrationCompleteDialogEnabled: this
            .isRegistrationCompleteDialogEnabled,
          isUpdateStatusDialogEnabled: this.isUpdateStatusDialogEnabled,
          isIntegrationStartedDialogEnabled: this
            .isIntegrationStartedDialogEnabled,
          isDeleteSpotWorkersDialogEnabled: this
            .isDeleteSpotWorkersDialogEnabled,
          handleRegisterResidentWorkersMenuButtonClicked: this
            .handleRegisterResidentWorkersMenuButtonClicked,
          handleRegisterResidentWorkersConfirmButtonClicked: this
            .handleRegisterResidentWorkersConfirmButtonClicked,
          handleRegistrationCompleteCloseButtonClicked: this
            .handleRegistrationCompleteCloseButtonClicked,
          handleUpdateStatusConfirmButtonClicked: this
            .handleUpdateStatusConfirmButtonClicked,
          handleDeleteSpotWorkersConfirmButtonClicked: this
            .handleDeleteSpotWorkersConfirmButtonClicked,
          searchFormHandle: this.searchFormHandle,
          closeDialog: this.closeDialog,
          closeErrorDialog: this.closeErrorDialog,
          clearSearchProperty: this.clearSearchProperty,
          inputSpotWorkerName: this.inputSpotWorkerName,
          inputCompanyName: this.inputCompanyName,
          checkSingleData: this.checkSingleData,
          doSearch: this.doSearch,
          changeSort: this.changeSort,
          handleSpotWorkerSelected: this.handleSpotWorkerSelected,
          canExecuteIntegration: this.canExecuteIntegration,
          userSelectedHandler: this.userSelectedHandler,
          unregisteredUsersInfiniteScrollLoad: this
            .unregisteredUsersInfiniteScrollLoad,
          showStatusDialog: this.showStatusDialog,
          showDeleteSpotWorkersDialog: this.showDeleteSpotWorkersDialog,
          changeStatus: this.changeStatus,
          executeIntegration: this.executeIntegration,
        }}
      >
        {this.props.children}
      </SpotWorkersContext.Provider>
    );
  }
}



// WEBPACK FOOTER //
// ./src/services/spotWorkers/SpotWorkersStore.js